import { computed } from "../../core/signals/primitives.js";
import { defineBehavior } from "../../core/composition/define-behavior.js";
import { createMachineReactor } from "../../core/reactors/create-machine-reactor.js";
import { waitForMediaSourceOpen } from "../../media/dom/mse/mediasource-setup.js";
import { getMaxBufferedEnd, shouldUpdateDuration, waitForSourceBuffersReady } from "../../media/dom/mse/duration.js";
//#region src/playback/behaviors/dom/update-mediasource-duration.ts
/**
* **Propagate `presentation.duration` to `mediaSource.duration` — exactly once per MediaSource.**
*
* Two paths, by whether the presentation is live:
*
* - **Live** (`presentation.duration === Infinity`): write `Infinity` once the MediaSource is open and no SourceBuffer is
*   mid-append. No buffered clamp is needed (`Infinity` ≥ any range) and — unlike the finite case — it needn't precede
*   the first append: `Infinity` overrides whatever finite live-edge value an append may have pinned (a finite duration
*   would otherwise stall the stream once the window slides past it). The wait-for-idle is required because the MSE
*   spec forbids setting `duration` while a buffer is `updating`; both waits resolve immediately when already open /
*   idle.
* - **VoD** (finite): the value is written once, after `mediaSource` is open and all SourceBuffers are idle, clamped to
*   be ≥ the highest buffered range (MSE spec). Written only while `mediaSource.duration` is still `NaN`; once any
*   non-NaN value is present (us on a prior entry, or `endOfStream` from the buffered end), the property is left alone
*   — re-syncing would race concurrent `appendBuffer()` calls.
*
* The entry resolves three async preconditions in order before writing:
*
* 1. **MediaSource open** — `waitForMediaSourceOpen` defers until the first `sourceopen` event (or any readyState
*    transition, since `'ended'` / `'closed'` mean we've missed the window and should bail).
* 2. **All SourceBuffers idle** — `waitForSourceBuffersReady` defers per the MSE-spec rule that `duration` cannot be set
*    while any buffer has `updating === true`.
* 3. **Buffered-range clamp** — `getMaxBufferedEnd` ensures the written duration is at least the highest buffered end (MSE
*    spec disallows a smaller `duration` than any buffered range).
*
* The buffer-set helpers operate across `mediaSource.sourceBuffers` (the canonical aggregate), so the behavior composes
* uniformly across audio-only, video-only, and mixed configurations.
*
* Single-positive-state reactor (`'preconditions-unmet'` ↔ `'duration-writable'`): state derivation is purely
* signal-driven (presentation validity + mediaSource existence). MediaSource lifecycle state (`readyState`) and the
* `duration` itself are non-signal DOM properties resolved inside the entry's async sequence. The state-exit cleanup
* aborts the in-flight wait, so source resets and behavior destroy structurally cancel the pending write. A post-await
* re-check of `mediaSource.readyState === 'open'` covers the narrow race where `endOfStream()` synchronously
* transitions readyState to `'ended'` between our `waitForMediaSourceOpen` resolution and the `mediaSource.duration`
* write.
*
* Downstream of `calculatePresentationDuration` (which writes `presentation.duration`); concurrent with `endOfStream`
* (which may later write `mediaSource.duration` — the "exactly once" contract keeps us out of that path).
*/
function deriveState(presentation, mediaSource) {
	return shouldUpdateDuration(presentation, mediaSource) ? "duration-writable" : "preconditions-unmet";
}
function updateMediaSourceDurationSetup({ state, context }) {
	const derivedStateSignal = computed(() => deriveState(state.presentation.get(), context.mediaSource.get()));
	return createMachineReactor({
		initial: "preconditions-unmet",
		monitor: () => derivedStateSignal.get(),
		states: {
			"preconditions-unmet": {},
			"duration-writable": { entry: () => {
				const presentation = state.presentation.get();
				const mediaSource = context.mediaSource.get();
				if (presentation.duration === Number.POSITIVE_INFINITY) {
					if (mediaSource.duration === Number.POSITIVE_INFINITY) return;
					const controller = new AbortController();
					(async () => {
						await waitForMediaSourceOpen(mediaSource, controller.signal);
						if (controller.signal.aborted || mediaSource.readyState !== "open") return;
						await waitForSourceBuffersReady(mediaSource.sourceBuffers, controller.signal);
						if (controller.signal.aborted || mediaSource.readyState !== "open") return;
						if (mediaSource.duration !== Number.POSITIVE_INFINITY) mediaSource.duration = Number.POSITIVE_INFINITY;
					})();
					return () => controller.abort();
				}
				if (!Number.isNaN(mediaSource.duration)) return;
				const controller = new AbortController();
				const writeWhenReady = async () => {
					await waitForMediaSourceOpen(mediaSource, controller.signal);
					if (controller.signal.aborted) return;
					if (mediaSource.readyState !== "open") return;
					await waitForSourceBuffersReady(mediaSource.sourceBuffers, controller.signal);
					if (controller.signal.aborted) return;
					if (mediaSource.readyState !== "open") return;
					const maxBufferedEnd = getMaxBufferedEnd(mediaSource.sourceBuffers);
					const duration = maxBufferedEnd > presentation.duration ? maxBufferedEnd : presentation.duration;
					if (Number.isNaN(mediaSource.duration)) mediaSource.duration = duration;
				};
				writeWhenReady();
				return () => controller.abort();
			} }
		}
	});
}
const updateMediaSourceDuration = defineBehavior({
	stateKeys: ["presentation"],
	contextKeys: ["mediaSource"],
	setup: updateMediaSourceDurationSetup
});
//#endregion
export { updateMediaSourceDuration };

//# sourceMappingURL=update-mediasource-duration.js.map